import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, Car, Home, Settings, PawPrint, User, Package, 
  MoreVertical, Edit, Trash2, Star, ChevronDown, ChevronUp,
  Loader2, AlertCircle
} from 'lucide-react';
import { customerAssetsApi } from '../../services/api';
import { useBusinessContext } from '../../contexts/BusinessContext';
import { industryService } from '../../services/industryService';
import { 
  industryUsesAssets, 
  getAssetTypesForIndustry, 
  getAssetFieldSchema,
  parseAttributes,
  AssetTypes 
} from '../../config/assetTemplates';
import AssetForm from './AssetForm';
import ConfirmModal from '../ConfirmModal';

/**
 * Icon mapping for asset types
 */
const AssetTypeIcon = ({ type, size = 18, className = '' }) => {
  const icons = {
    Vehicle: Car,
    Property: Home,
    Equipment: Settings,
    Pet: PawPrint,
    Person: User,
    Other: Package,
  };
  const Icon = icons[type] || Package;
  return <Icon size={size} className={className} />;
};

/**
 * CustomerAssetsTab - Displays and manages assets for a customer
 * 
 * Only shows if the business's industry uses assets.
 * Adapts to show relevant asset types based on industry.
 */
const CustomerAssetsTab = ({ customerId, customerName, onCountChange }) => {
  const [industryType, setIndustryType] = useState('Other');
  
  // Fetch business industry type
  useEffect(() => {
    const fetchIndustry = async () => {
      try {
        const theme = await industryService.getMyTheme();
        setIndustryType(theme?.serviceType || 'Other');
      } catch (err) {
        console.log('Could not fetch industry:', err);
      }
    };
    fetchIndustry();
  }, []);
  
  // Check if this industry uses assets
  const usesAssets = industryUsesAssets(industryType);
  const availableAssetTypes = getAssetTypesForIndustry(industryType);
  
  // State
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Form modal state
  const [showForm, setShowForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [defaultAssetType, setDefaultAssetType] = useState(null);
  
  // Delete confirm state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, asset: null });
  const [deleting, setDeleting] = useState(false);
  
  // Expanded sections (for grouping by type)
  const [expandedTypes, setExpandedTypes] = useState({});

  // Load assets
  const loadAssets = useCallback(async () => {
    if (!customerId || !usesAssets) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await customerAssetsApi.getByCustomer(customerId, { includeInactive: false });
      setAssets(response.data || []);
      onCountChange?.((response.data || []).length);
      
      // Auto-expand types that have assets
      const typesWithAssets = {};
      (response.data || []).forEach(a => {
        typesWithAssets[a.assetType] = true;
      });
      setExpandedTypes(typesWithAssets);
    } catch (err) {
      console.error('Error loading assets:', err);
      setError('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [customerId, usesAssets]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  // Handlers
  const handleAddAsset = (assetType = null) => {
    setEditingAsset(null);
    setDefaultAssetType(assetType || availableAssetTypes[0] || AssetTypes.Other);
    setShowForm(true);
  };

  const handleEditAsset = (asset) => {
    setEditingAsset(asset);
    setDefaultAssetType(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingAsset(null);
    setDefaultAssetType(null);
  };

  const handleFormSave = async (formData) => {
    try {
      if (editingAsset) {
        await customerAssetsApi.update(customerId, editingAsset.id, {
          id: editingAsset.id,
          ...formData
        });
      } else {
        await customerAssetsApi.create(customerId, formData);
      }
      await loadAssets();
      handleFormClose();
    } catch (err) {
      console.error('Error saving asset:', err);
      throw err; // Let form handle the error display
    }
  };

  const handleDeleteClick = (asset) => {
    setDeleteConfirm({ open: true, asset });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.asset) return;
    
    try {
      setDeleting(true);
      await customerAssetsApi.delete(customerId, deleteConfirm.asset.id);
      await loadAssets();
      setDeleteConfirm({ open: false, asset: null });
    } catch (err) {
      console.error('Error deleting asset:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleSetPrimary = async (asset) => {
    try {
      await customerAssetsApi.setPrimary(customerId, asset.id);
      await loadAssets();
    } catch (err) {
      console.error('Error setting primary:', err);
    }
  };

  const toggleTypeExpanded = (type) => {
    setExpandedTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Don't render if industry doesn't use assets
  if (!usesAssets) {
    return null;
  }

  // Group assets by type
  const assetsByType = {};
  availableAssetTypes.forEach(type => {
    assetsByType[type] = assets.filter(a => a.assetType === type);
  });

  // Get display info for asset type
  const pluralize = (w) => (w.endsWith('y') ? w.slice(0, -1) + 'ies' : w + 's');

  const getTypeInfo = (type) => {
    const schema = getAssetFieldSchema(type);
    return {
      displayName: schema.displayName,
      icon: type,
    };
  };

  // Format attribute summary for display
  const getAttributeSummary = (asset) => {
    const attrs = parseAttributes(asset.attributesJson);
    const summaryParts = [];
    
    switch (asset.assetType) {
      case AssetTypes.Vehicle:
        if (attrs.year) summaryParts.push(attrs.year);
        if (attrs.make) summaryParts.push(attrs.make);
        if (attrs.model) summaryParts.push(attrs.model);
        if (attrs.licensePlate) summaryParts.push(`• ${attrs.licensePlate}`);
        break;
        
      case AssetTypes.Property:
        {
          const cityLine = [attrs.city, attrs.state, attrs.zipCode].filter(Boolean).join(', ');
          if (cityLine) summaryParts.push(cityLine);
        }
        if (attrs.squareFeet) summaryParts.push(`${attrs.squareFeet.toLocaleString()} sq ft`);
        if (attrs.stories) summaryParts.push(`${attrs.stories} story`);
        if (attrs.propertyType) summaryParts.push(attrs.propertyType);
        break;
        
      case AssetTypes.Equipment:
        if (attrs.manufacturer) summaryParts.push(attrs.manufacturer);
        if (attrs.model) summaryParts.push(attrs.model);
        if (attrs.serialNumber) summaryParts.push(`S/N: ${attrs.serialNumber}`);
        break;
        
      case AssetTypes.Pet:
        if (attrs.breed) summaryParts.push(attrs.breed);
        if (attrs.weight) summaryParts.push(`${attrs.weight} lbs`);
        if (attrs.temperament) summaryParts.push(attrs.temperament);
        break;
        
      case AssetTypes.Person:
        if (attrs.hairType) summaryParts.push(attrs.hairType);
        if (attrs.skinType) summaryParts.push(`Skin: ${attrs.skinType}`);
        break;
        
      default:
        if (attrs.category) summaryParts.push(attrs.category);
    }
    
    return summaryParts.join(' • ') || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12 text-red-500">
        <AlertCircle className="w-5 h-5 mr-2" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Add button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Assets</h3>
          <p className="text-sm text-gray-500">
            Track {availableAssetTypes.length === 1 
              ? pluralize(getTypeInfo(availableAssetTypes[0]).displayName.toLowerCase())
              : 'items'} for this customer
          </p>
        </div>
        
        {availableAssetTypes.length === 1 ? (
          <button
            onClick={() => handleAddAsset(availableAssetTypes[0])}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus size={16} />
            Add {getTypeInfo(availableAssetTypes[0]).displayName}
          </button>
        ) : (
          <div className="relative group">
            <button className="btn btn-primary flex items-center gap-2">
              <Plus size={16} />
              Add Asset
              <ChevronDown size={14} />
            </button>
            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              {availableAssetTypes.map(type => {
                const info = getTypeInfo(type);
                return (
                  <button
                    key={type}
                    onClick={() => handleAddAsset(type)}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <AssetTypeIcon type={type} size={16} className="text-gray-400" />
                    {info.displayName}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Asset list by type */}
      {assets.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <Package className="mx-auto h-10 w-10 text-gray-300 mb-3" />
          <p className="text-gray-500 mb-2">No assets yet</p>
          <p className="text-sm text-gray-400">
            Add {availableAssetTypes.length === 1 
              ? `a ${getTypeInfo(availableAssetTypes[0]).displayName.toLowerCase()}`
              : 'assets'} to track service history
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {availableAssetTypes.map(type => {
            const typeAssets = assetsByType[type] || [];
            const info = getTypeInfo(type);
            const isExpanded = expandedTypes[type] !== false; // Default to expanded
            
            if (typeAssets.length === 0 && availableAssetTypes.length === 1) {
              return null; // Don't show empty section for single-type industries
            }
            
            return (
              <div key={type} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Type header - only show if multiple types */}
                {availableAssetTypes.length > 1 && (
                  <button
                    onClick={() => toggleTypeExpanded(type)}
                    className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <AssetTypeIcon type={type} size={18} className="text-gray-500" />
                      <span className="font-medium text-gray-900">{pluralize(info.displayName)}</span>
                      <span className="text-sm text-gray-500">({typeAssets.length})</span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp size={18} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={18} className="text-gray-400" />
                    )}
                  </button>
                )}
                
                {/* Asset items */}
                {(isExpanded || availableAssetTypes.length === 1) && (
                  <div className="divide-y divide-gray-100">
                    {typeAssets.length === 0 ? (
                      <div className="px-4 py-6 text-center text-gray-400 text-sm">
                        No {info.displayName.toLowerCase()}s added
                      </div>
                    ) : (
                      typeAssets.map(asset => (
                        <AssetRow
                          key={asset.id}
                          asset={asset}
                          summary={getAttributeSummary(asset)}
                          onEdit={() => handleEditAsset(asset)}
                          onDelete={() => handleDeleteClick(asset)}
                          onSetPrimary={() => handleSetPrimary(asset)}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Asset Form Modal */}
      {showForm && (
        <AssetForm
          isOpen={showForm}
          onClose={handleFormClose}
          onSave={handleFormSave}
          asset={editingAsset}
          defaultAssetType={defaultAssetType}
          availableAssetTypes={availableAssetTypes}
          customerName={customerName}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, asset: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Asset"
        message={
          deleteConfirm.asset?.jobCount > 0
            ? `This ${deleteConfirm.asset?.assetType?.toLowerCase() || 'asset'} has ${deleteConfirm.asset.jobCount} job(s) in history. It will be deactivated but kept for records.`
            : `Are you sure you want to delete "${deleteConfirm.asset?.name}"? This cannot be undone.`
        }
        confirmText={deleteConfirm.asset?.jobCount > 0 ? 'Deactivate' : 'Delete'}
        variant="danger"
        loading={deleting}
      />
    </div>
  );
};

/**
 * Individual asset row component
 */
const AssetRow = ({ asset, summary, onEdit, onDelete, onSetPrimary }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <div className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors group">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <AssetTypeIcon type={asset.assetType} size={20} className="text-gray-500" />
          </div>
          
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 truncate">
                {(asset.assetType === 'Property' && parseAttributes(asset.attributesJson).address) || asset.name}
              </span>
              {asset.isPrimary && (
                <span className="flex-shrink-0 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium flex items-center gap-1">
                  <Star size={10} fill="currentColor" />
                  Primary
                </span>
              )}
            </div>
            
            {summary && (
              <p className="text-sm text-gray-500 truncate">{summary}</p>
            )}
            
            {asset.jobCount > 0 && (
              <p className="text-xs text-gray-400 mt-0.5">
                {asset.jobCount} job{asset.jobCount !== 1 ? 's' : ''} on record
              </p>
            )}
          </div>
        </div>

        {/* Actions button */}
        <button
          onClick={() => setMenuOpen(true)}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg sm:opacity-0 sm:group-hover:opacity-100 transition-all"
        >
          <MoreVertical size={18} />
        </button>
      </div>

      {/* Mobile-friendly action sheet / Desktop dropdown */}
      {menuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40 bg-black/20 sm:bg-transparent" 
            onClick={() => setMenuOpen(false)} 
          />
          
          {/* Menu - Bottom sheet on mobile, positioned dropdown on desktop */}
          <div className="fixed sm:absolute inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-auto sm:right-4 sm:top-auto z-50">
            {/* Mobile bottom sheet */}
            <div className="sm:hidden bg-white rounded-t-2xl shadow-2xl border-t border-gray-200 safe-area-pb">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-2" />
              <div className="px-2 pb-4">
                <div className="px-4 py-2 mb-2">
                  <p className="font-medium text-gray-900">{asset.name}</p>
                  {summary && <p className="text-sm text-gray-500">{summary}</p>}
                </div>
                
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit();
                  }}
                  className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-3"
                >
                  <Edit size={18} className="text-gray-400" />
                  Edit
                </button>
                
                {!asset.isPrimary && (
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      onSetPrimary();
                    }}
                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-3"
                  >
                    <Star size={18} className="text-gray-400" />
                    Set as Primary
                  </button>
                )}
                
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete();
                  }}
                  className="w-full px-4 py-3 text-left text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-3"
                >
                  <Trash2 size={18} />
                  Delete
                </button>
                
                <button
                  onClick={() => setMenuOpen(false)}
                  className="w-full mt-2 px-4 py-3 text-center text-gray-500 hover:bg-gray-100 rounded-lg font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
            
            {/* Desktop dropdown */}
            <div className="hidden sm:block w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 mt-1">
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onEdit();
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <Edit size={14} />
                Edit
              </button>
              
              {!asset.isPrimary && (
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onSetPrimary();
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Star size={14} />
                  Set as Primary
                </button>
              )}
              
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onDelete();
                }}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CustomerAssetsTab;